import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { dsUseIsMobile } from '@coloplast/design-system';

const DEFAULT_STICKY_HEADER_HEIGHT = 82;
const MAX_ATTEMPTS = 5;

const stickyHeaderSelectors = [
	'.ds-header.ds-header--sticky',
	'.ds-header-mobile.ds-header--sticky',
	".c-nav-menu[data-sticky='true']",
];

export const useStickyHeaderHeight = () => {
	const headerHeight = ref(DEFAULT_STICKY_HEADER_HEIGHT);
	const hasStickyHeader = ref(true);
	const headerElem = ref<HTMLElement | null>(null);
	const { isMobile } = dsUseIsMobile();

	let headerElemAttempts = MAX_ATTEMPTS;
	let headerTimeout: ReturnType<typeof globalThis.setTimeout> | null = null;
	let responsiveTimeout: ReturnType<typeof globalThis.setTimeout> | null = null;
	let resizeObserver: ResizeObserver | null = null;

	const clearHeaderTimeout = () => {
		if (headerTimeout) {
			clearTimeout(headerTimeout);
			headerTimeout = null;
		}
	};

	const clearResponsiveTimeout = () => {
		if (responsiveTimeout) {
			clearTimeout(responsiveTimeout);
			responsiveTimeout = null;
		}
	};

	const disconnectResizeObserver = () => {
		if (resizeObserver) {
			resizeObserver.disconnect();
			resizeObserver = null;
		}
	};

	const isVisible = (element: HTMLElement) => {
		const elementRect = element.getBoundingClientRect();
		const elementStyles = globalThis.getComputedStyle(element);

		return (
			elementRect.height > 0 &&
			elementStyles.display !== 'none' &&
			elementStyles.visibility !== 'hidden'
		);
	};

	const getVisibleStickyHeader = () => {
		const stickyHeaders = stickyHeaderSelectors
			.flatMap(selector => Array.from(document.querySelectorAll<HTMLElement>(selector)));

		return stickyHeaders.find(isVisible) ?? null;
	};

	const setHeaderHeight = () => {
		const visibleStickyHeader = getVisibleStickyHeader();

		headerElem.value = visibleStickyHeader;
		hasStickyHeader.value = !!visibleStickyHeader;

		if (visibleStickyHeader) {
			headerHeight.value = visibleStickyHeader.getBoundingClientRect().height;
		} else {
			headerHeight.value = 0;
		}
	};

	const observeHeaderHeight = () => {
		disconnectResizeObserver();

		if (!headerElem.value) return;

		resizeObserver = new ResizeObserver(setHeaderHeight);
		resizeObserver.observe(headerElem.value);
	};

	const getReferenceToHeader = () => {
		setHeaderHeight();
		observeHeaderHeight();

		if (!headerElem.value && headerElemAttempts) {
			headerElemAttempts--;
			headerTimeout = globalThis.setTimeout(getReferenceToHeader, 1000);
		}
	};

	const resetHeaderAttempts = () => {
		headerElemAttempts = MAX_ATTEMPTS;
	};

	const handleResize = () => {
		setHeaderHeight();
		observeHeaderHeight();
	};

	onMounted(() => {
		getReferenceToHeader();
		globalThis.addEventListener('resize', handleResize);
	});

	onBeforeUnmount(() => {
		clearHeaderTimeout();
		clearResponsiveTimeout();
		disconnectResizeObserver();
		globalThis.removeEventListener('resize', handleResize);
	});

	watch(
		() => isMobile.value,
		() => {
			clearResponsiveTimeout();

			responsiveTimeout = globalThis.setTimeout(() => {
				resetHeaderAttempts();
				getReferenceToHeader();
			}, 500);
		}
	);

	return { headerHeight, hasStickyHeader, headerElem };
};